/**
 * [A] 차량 관리 — 외주(gtli) 관리자 "차량 관리"(회원 구독 차량 목록) 재현.
 *
 * 컬럼(벤더 순서): 이메일·이름·차량번호·상품명·하부세차·시작일자·만료일자·세차횟수·오늘세차·결제상태·비고.
 * 동작: 컬럼 정렬 · 만료(period_end<now) 행 노랑 하이라이트 · 기간+차량번호 필터.
 * 데이터: GET /owner-console/modernwash/vendor-parity/vehicle-list (live read-only).
 *
 * ⚠️ 운영 승인(1회 예외): 회원 email 등 민감 컬럼 노출. 접근은 백엔드가 master/연동매장(jeonju) 매니저로 강제.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import QueryError from '../../components/QueryError';
import { getAdminRole } from '../../utils/auth';
import { getVendorVehicleList } from '../../api/modernwashVendor';
import {
  surfaceSx,
  kstDate,
  formatVendorDate,
  isExpired,
  SourceBanner,
  shouldRenderBody,
  useMasterStores,
  useColumnSort,
  DateField,
  TextField,
  StoreSelect,
} from './vendorParityCommon';

const COLUMNS = [
  { key: 'email', label: '이메일', mono: false },
  { key: 'name', label: '이름' },
  { key: 'licensePlate', label: '차량번호', mono: true },
  { key: 'productionName', label: '상품명' },
  { key: 'underbodyWash', label: '하부세차', align: 'center' },
  { key: 'periodStart', label: '시작일자', fmt: formatVendorDate },
  { key: 'periodEnd', label: '만료일자', fmt: formatVendorDate },
  { key: 'washCount', label: '세차횟수', align: 'right' },
  { key: 'todayWashed', label: '오늘세차', align: 'center' },
  { key: 'gubun', label: '결제상태', align: 'center' },
  { key: 'contents', label: '비고' },
];

const SortHeader = ({ col, sort, onSort }) => (
  <TableCell
    align={col.align || 'left'}
    onClick={() => onSort(col.key)}
    sx={{ whiteSpace: 'nowrap', fontWeight: 700, cursor: 'pointer', userSelect: 'none' }}
  >
    {col.label}
    {sort.key === col.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
  </TableCell>
);

const cellValue = (row, col) => {
  const v = row[col.key];
  if (col.fmt) return col.fmt(v);
  if (typeof v === 'boolean') return v ? 'y' : 'N';
  if (v == null || v === '') return '';
  return String(v);
};

const VendorVehicleListPage = () => {
  const navigate = useNavigate();
  const role = getAdminRole();
  const isMaster = role === 'master';
  const today = kstDate(0);

  // vehicle_list 는 구독 차량 전체가 기본(벤더 기본 범위와 동일하게 넓게).
  const [startDate, setStartDate] = useState('2016-01-01');
  const [endDate, setEndDate] = useState(today);
  const [lpInput, setLpInput] = useState('');
  const [targetLp, setTargetLp] = useState('');
  const rangeInvalid = startDate > endDate;

  const stores = useMasterStores(isMaster);
  const [storeId, setStoreId] = useState('');
  const storeParam = isMaster ? storeId || undefined : undefined;

  const [state, setState] = useState({ loading: true, error: null, res: null });
  const load = useCallback(() => {
    if (rangeInvalid) return undefined;
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    getVendorVehicleList({ storeId: storeParam, start: startDate, end: endDate, targetLp: targetLp || undefined })
      .then((res) => alive && setState({ loading: false, error: null, res }))
      .catch((e) => alive && setState({ loading: false, error: e, res: null }));
    return () => {
      alive = false;
    };
  }, [storeParam, startDate, endDate, targetLp, rangeInvalid]);
  useEffect(() => load(), [load]);

  const res = state.res;
  const items = res?.items || [];
  const { sorted, sort, toggle } = useColumnSort(items);
  const showBody = shouldRenderBody(res) && !state.loading && !state.error;

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, sm: 3, lg: 4 }, py: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <IconButton size="small" onClick={() => navigate('/modernwash')} aria-label="ModernWash 운영으로">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: 20, md: 28 }, fontWeight: 800, letterSpacing: '-0.03em' }}>
            차량 관리
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            외주 관리자 회원 구독 차량 목록 · 만료 행은 노랑 표시
          </Typography>
        </Box>
        <Box sx={{ ml: { md: 'auto' }, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            size="small"
            label={isMaster ? '전체 매장 조회 (master)' : '본인 매장 고정 (manager)'}
            variant="outlined"
            sx={{ height: 30, borderRadius: 999, fontWeight: 700 }}
          />
          <IconButton size="small" onClick={load} aria-label="새로고침">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <SourceBanner res={res} />
      </Box>

      <Paper variant="outlined" sx={{ ...surfaceSx, p: { xs: 1.75, md: 2 }, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <DateField label="시작일" value={startDate} max={endDate} onChange={setStartDate} />
          <DateField label="종료일" value={endDate} min={startDate} max={today} onChange={setEndDate} />
          <TextField
            label="차량번호"
            value={lpInput}
            placeholder="예: 12가3456"
            onChange={setLpInput}
            onEnter={() => setTargetLp(lpInput.trim())}
          />
          <Box
            component="button"
            type="button"
            onClick={() => setTargetLp(lpInput.trim())}
            sx={{
              height: 34,
              px: 2,
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#7C3AED',
              color: '#fff',
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            검색
          </Box>
          {isMaster ? <StoreSelect stores={stores} value={storeId} onChange={setStoreId} /> : null}
        </Box>
        {rangeInvalid ? (
          <Typography sx={{ fontSize: 12.5, color: 'error.main', mt: 1 }}>시작일이 종료일보다 늦습니다.</Typography>
        ) : null}
      </Paper>

      {state.error ? (
        <QueryError error={state.error} onRetry={load} context="차량 관리" />
      ) : state.loading ? (
        <LinearProgress sx={{ mb: 2 }} />
      ) : null}

      {showBody ? (
        <Paper variant="outlined" sx={{ ...surfaceSx, p: { xs: 1.5, md: 2 } }}>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 1.5 }}>
            총 {items.length.toLocaleString()}건 · 헤더 클릭 시 정렬
          </Typography>
          {items.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>조건에 맞는 차량이 없습니다.</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 620 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {COLUMNS.map((col) => (
                      <SortHeader key={col.key} col={col} sort={sort} onSort={toggle} />
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sorted.map((row, i) => {
                    const expired = isExpired(row.periodEnd);
                    return (
                      <TableRow
                        key={`${row.licensePlate}-${row.email}-${i}`}
                        hover
                        sx={expired ? { backgroundColor: alpha('#FDE047', 0.35) } : undefined}
                      >
                        {COLUMNS.map((col) => (
                          <TableCell
                            key={col.key}
                            align={col.align || 'left'}
                            sx={{
                              whiteSpace: 'nowrap',
                              fontFamily: col.mono ? 'monospace' : 'inherit',
                            }}
                          >
                            {cellValue(row, col)}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      ) : null}
    </Box>
  );
};

export default VendorVehicleListPage;
